import React, { useState, ReactNode } from "react";
import { handleClickSignIn, handleClickConfirmSignIn } from "../lib/auth";

// jotai
import { useSetAtom } from "jotai";
import { AppAtoms } from "../lib/store";

// ui
import {
  InputAdornment,
  Alert,
  Input,
  IconButton,
  FormControl,
  Box,
  FormHelperText,
} from "@mui/material";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Visibility from "@mui/icons-material/Visibility";
import { LoadingButton } from "@mui/lab";
import SaveIcon from "@mui/icons-material/Save";
import "../component/Login.css";

// api
import axios from "axios";
import { getProfile } from "../api/profile";
import { getScreenColumns } from "../api/screenColumn";
import { CONST } from "../config/const";

// 認証確認
import { fetchAuth } from "../lib/auth";

function Login() {
  const setIsLogin = useSetAtom(AppAtoms.isLogin);
  const setLoginUser = useSetAtom(AppAtoms.loginUser);
  const setContractScreenColumn = useSetAtom(AppAtoms.contractScreenColumn);
  const setTossUpScreenColumn = useSetAtom(AppAtoms.tossUpScreenColumn);
  const setProfileLoaded = useSetAtom(AppAtoms.profileLoaded);
  const setTenantPlan = useSetAtom(AppAtoms.tenantPlanAtom);
  const setAccountRunkingScreenColumn = useSetAtom(AppAtoms.accountRunkingScreenColumn);
  const setSampleQuestions = useSetAtom(AppAtoms.sampleQuestionsAtom);

  // ログイン画面かパスワード変更画面かのフラグ
  const [onLogin, setOnLogin] = useState<boolean>(true);
  //ログイン画面
  const [Id, setId] = useState<string>("");
  const [pass, setPass] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);

  //パスワード変更画面
  const [newpass1, setNewpass1] = useState<string>("");
  const [showPassword1, setShowPassword1] = useState<boolean>(false);
  const [newpass2, setNewpass2] = useState<string>("");
  const [showPassword2, setShowPassword2] = useState<boolean>(false);

  const [message, setMessage] = useState<ReactNode>("");
  const [loading, setLoading] = useState<boolean>(false);

  async function getScreenColumn() {
    const {
      contractScreenColumn,
      tossUpScreenColumn,
      accountRunkingScreenColumn,
    } = await getScreenColumns();
    setContractScreenColumn(contractScreenColumn);
    setTossUpScreenColumn(tossUpScreenColumn);
    setAccountRunkingScreenColumn(accountRunkingScreenColumn);
  }
  async function login() {
    setLoading(true);
    const signInRes: string = await handleClickSignIn(Id, pass);
    if (signInRes === "Login") {
      const { login, loginUser } = await fetchAuth();
      setLoginUser(loginUser);
      setIsLogin(login);
      if (loginUser) {
        getScreenColumn();
        axios.get(CONST.apiUrl2 + "sampleQuestions")
          .then((res) => {
            if (res.data.questions_by_page) {
              setSampleQuestions(res.data.questions_by_page);
            }
          })
          .catch(() => {});
        try {
          const profile = await getProfile();
          setLoginUser({
            ...loginUser,
            db_authority: profile.authority,
            username: profile.account_name,
          });
          setTenantPlan(
            profile.plan_id != null
              ? {
                  plan_id: profile.plan_id,
                  plan_name: profile.plan_name ?? "",
                  max_partners: profile.max_partners ?? 0,
                  max_et_sites: profile.max_et_sites ?? 0,
                  has_payment: profile.has_payment ?? false,
                  has_ai_analysis: profile.has_ai_analysis ?? false,
                  current_partners: profile.current_partners ?? 0,
                  current_et_sites: profile.current_et_sites ?? 0,
                  monthly_price: profile.monthly_price ?? 0,
                }
              : null,
          );
        } finally {
          setProfileLoaded(true);
        }
      }
    } else if (signInRes === "NextStep") {
      setIsLogin(false);
      setOnLogin(false);
      setMessage("初期パスワード変更してください。");
    } else if (signInRes === "Error") {
      setIsLogin(false);
      setMessage("IDまたはパスワードが違います。");
    }
    setLoading(false);
  }

  async function changePassword() {
    if (!newpass1 || !newpass2) {
      setMessage(
        <div style={{ marginBottom: "30px" }}>
          <div>新規パスワードを入力してください。</div>
        </div>
      );
      return;
    } else if (newpass1 !== newpass2) {
      setMessage(
        <>
          新規パスワードが確認用パスワードと
          <br />
          一致していません。
        </>
      );
      return;
    } else if (newpass1 === pass) {
      setMessage(
        <>
          現在使用しているパスワードは
          <br />
          新規パスワードに指定できません。
        </>
      );
      return;
    }
    setLoading(true);
    const signInRes = await handleClickConfirmSignIn(newpass1);
    if (signInRes === "Login") {
      const { login, loginUser } = await fetchAuth();
      setLoginUser(loginUser);
      setIsLogin(login);
      if (loginUser) {
        getScreenColumn();
        axios.get(CONST.apiUrl2 + "sampleQuestions")
          .then((res) => {
            if (res.data.questions_by_page) {
              setSampleQuestions(res.data.questions_by_page);
            }
          })
          .catch(() => {});
        try {
          const profile = await getProfile();
          setLoginUser({
            ...loginUser,
            db_authority: profile.authority,
            username: profile.account_name,
          });
          setTenantPlan(
            profile.plan_id != null
              ? {
                  plan_id: profile.plan_id,
                  plan_name: profile.plan_name ?? "",
                  max_partners: profile.max_partners ?? 0,
                  max_et_sites: profile.max_et_sites ?? 0,
                  has_payment: profile.has_payment ?? false,
                  has_ai_analysis: profile.has_ai_analysis ?? false,
                  current_partners: profile.current_partners ?? 0,
                  current_et_sites: profile.current_et_sites ?? 0,
                  monthly_price: profile.monthly_price ?? 0,
                }
              : null,
          );
        } finally {
          setProfileLoaded(true);
        }
      }
    } else if (signInRes === "Error") {
      setIsLogin(false);
      setMessage(
        <>
          パスワードの要件を満たしていません。
          <br />
          大文字・小文字・数字・記号を含む
          <br />
          8文字以上の文字列をご入力ください。
        </>
      );
    }
    setLoading(false);
  }

  return (
    <Box className="backgroundImage" style={{ minHeight: "544px" }}>
      <form
        style={{ backgroundColor: "rgba(255, 255, 255, 0.7)" }}
        onSubmit={(e) => {
          e.preventDefault();
          if (onLogin) {
            login();
          } else {
            changePassword();
          }
        }}
      >
        <Box className="formDev">
          {onLogin ? (
            <>
              <FormControl
                variant="standard"
                style={{ width: "100%", margin: "30px 0px" }}
              >
                <FormHelperText
                  id="serch-name"
                  sx={{
                    position: "absolute",
                    bottom: 30,
                    fontSize: "16px",
                  }}
                >
                  ログインID
                </FormHelperText>
                <Input
                  id="inputId"
                  value={Id}
                  onChange={(e) => setId(e.target.value)}
                  style={{
                    width: "100%",
                    backgroundColor: "transparent",
                  }}
                />
              </FormControl>
              <FormControl
                variant="standard"
                style={{ width: "100%", margin: "20px 0px" }}
              >
                <FormHelperText
                  id="serch-name"
                  sx={{
                    position: "absolute",
                    bottom: 30,
                    fontSize: "16px",
                  }}
                >
                  パスワード
                </FormHelperText>
                <Input
                  id="inputPass"
                  value={pass}
                  onChange={(e) => setPass(e.target.value)}
                  type={showPassword ? "text" : "password"}
                  style={{
                    width: "100%",
                    backgroundColor: "transparent",
                  }}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        onMouseDown={(
                          event: React.MouseEvent<HTMLButtonElement>
                        ) => {
                          event.preventDefault();
                        }}
                        style={{ backgroundColor: "transparent" }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </FormControl>
            </>
          ) : (
            <>
              <FormControl
                variant="standard"
                style={{ width: "100%", margin: "30px 0px" }}
              >
                <FormHelperText
                  id="serch-name"
                  sx={{
                    position: "absolute",
                    bottom: 30,
                    fontSize: "16px",
                  }}
                >
                  新規パスワード
                </FormHelperText>
                <Input
                  id="inputPass"
                  value={newpass1}
                  onChange={(e) => setNewpass1(e.target.value)}
                  type={showPassword1 ? "text" : "password"}
                  style={{
                    width: "100%",
                    backgroundColor: "transparent",
                  }}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword1(!showPassword1)}
                        onMouseDown={(
                          event: React.MouseEvent<HTMLButtonElement>
                        ) => {
                          event.preventDefault();
                        }}
                        style={{ backgroundColor: "transparent" }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </FormControl>
              <FormControl
                variant="standard"
                style={{ width: "100%", margin: "20px 0px" }}
              >
                <FormHelperText
                  id="serch-name"
                  sx={{
                    position: "absolute",
                    bottom: 30,
                    fontSize: "16px",
                  }}
                >
                  新規パスワード（確認用）
                </FormHelperText>
                <Input
                  id="inputPass"
                  value={newpass2}
                  onChange={(e) => setNewpass2(e.target.value)}
                  type={showPassword2 ? "text" : "password"}
                  style={{
                    width: "100%",
                    backgroundColor: "transparent",
                  }}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword2(!showPassword2)}
                        onMouseDown={(
                          event: React.MouseEvent<HTMLButtonElement>
                        ) => {
                          event.preventDefault();
                        }}
                        style={{ backgroundColor: "transparent" }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </FormControl>
            </>
          )}

          {message !== "" ? (
            <Alert
              severity="error"
              sx={{ position: "absolute", width: 294, marginTop: "15px" }}
            >
              {message}
            </Alert>
          ) : null}
          <LoadingButton
            type="submit"
            loading={loading}
            loadingPosition="center"
            variant="contained"
            startIcon={loading ? <SaveIcon /> : null}
            size="large"
            style={{
              width: "100%",
              marginTop: "120px",
            }}
          >
            {onLogin ? "ログイン" : "パスワード変更"}
          </LoadingButton>
        </Box>
      </form>
    </Box>
  );
}

export default Login;
